<template>
  <div ref="appRootRef" class="app">
    <Teleport to="body">
      <div
          class="global-route-progress"
          :class="{ 'is-active': routeNavPending }"
          aria-hidden="true"
      >
        <div class="global-route-progress__track">
          <div class="global-route-progress__bar" />
        </div>
      </div>
    </Teleport>

    <div
        ref="topMsgStripRef"
        v-show="appTopMsgText"
        class="app-top-msg-strip"
        role="note"
    >
      <div class="app-top-msg-strip__ratio">
        <div class="app-top-msg-strip__inner">
          <span class="app-top-msg-strip__text">{{ appTopMsgText }}</span>
        </div>
      </div>
    </div>

    <div
        class="app-body"
        :class="{ 'app-body--flush-tab': flushTabBottom, 'app-body--trade': route.path === '/trade' }"
    >
      <div class="app-router-shell">
        <router-view v-slot="{ Component }">
          <transition :name="shellTransitionName">
            <keep-alive :max="12">
              <component
                  :is="Component"
                  v-if="Component"
                  :key="keepAliveKey"
                  class="app-view-root"
              />
            </keep-alive>
          </transition>
        </router-view>
      </div>
    </div>

    <van-tabbar v-model="active" route safe-area-inset-bottom>
      <van-tabbar-item to="/" icon="home-o">首页</van-tabbar-item>
      <van-tabbar-item to="/holdings" icon="gold-coin-o">市场</van-tabbar-item>
      <van-tabbar-item to="/data-hall" icon="chart-trending-o">数据统计</van-tabbar-item>
      <van-tabbar-item to="/trade" icon="cart-o">交易</van-tabbar-item>
      <van-tabbar-item to="/announcements" icon="volume-o">公告</van-tabbar-item>
      <van-tabbar-item replace to="/user" icon="manager-o">我的</van-tabbar-item>
      <van-tabbar-item to="/more" icon="apps-o">更多</van-tabbar-item>
    </van-tabbar>

    <div v-if="taskRunning" class="task-badge" @click="$router.push({ path: '/trade', query: { sub: 'purchase' } })">
      <van-loading size="14" color="#fff" /> 运行中
    </div>

    <FirstLaunchUserAgreement />

    <ActivationGuard />

    <ForceUpdate ref="forceUpdateRef" @reactivate="handleReactivate" />

    <TradeQuickFab
      :show="tradeFabVisible"
      :unique-g-id="fabUniqueGId"
      :album-id="fabAlbumId"
      :album-name="fabAlbumName"
      :g-num="fabGNum"
      :price="fabPrice"
      :issue-num="fabIssueNum"
      :sold-num="fabSoldNum"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { showDialog } from "vant";
import { getState } from "@/services/purchase";
import { setTokenExpiredHandler } from "@/api/lst";
import { validateLstToken, clearTokenCheckCache } from "@/utils/lstTokenGate";
import { getLstToken, getLstPhone, clearLstSession } from "@/utils/lstSessionStorage";
import { startHeartbeat, stopHeartbeat, clearSession, initDeviceSecurity } from "@/utils/appSecurity";
import { initIntegrityGuard } from "@/utils/integrityGuard";
import { startActivityAssistantScheduler } from "@/utils/activityAssistantSchedule";
import { startHallTradeScheduler } from "@/utils/hallTradeQueue";
import { startAutoOrderPayScheduler } from "@/utils/autoOrderPayScheduler";
import { useTradeFabRouteContext } from "@/composables/useTradeFabRouteContext";
import { startWarehousePeriodicSync } from "@/utils/warehouseSync";
import { routeNavPending } from "@/utils/pageLoading";
import { getPrimaryColor } from "@/utils/cssVars";
import { appTopMsgText, fetchAppTopMsgOnce } from "@/utils/appTopMsg";
import FirstLaunchUserAgreement from "@/components/FirstLaunchUserAgreement.vue";
import ActivationGuard from "@/views/Activationguard.vue";
import ForceUpdate from "@/views/ForceUpdate.vue";
import TradeQuickFab from "@/components/TradeQuickFab.vue";
const appRootRef = ref(null);
const topMsgStripRef = ref(null);
const route = useRoute();
const { uniqueGId: fabUniqueGId, albumId: fabAlbumId, albumName: fabAlbumName, gNum: fabGNum, price: fabPrice, issueNum: fabIssueNum, soldNum: fabSoldNum } = useTradeFabRouteContext();
const router = useRouter();
const flushTabBottom = computed(() => route.path.startsWith("/on-sale/"));
const tradeFabVisible = computed(() => route.path !== "/more");
const keepAliveKey = computed(() => {
    const path = route.path;
    if (path === "/trade")
        return path;
    return route.fullPath;
});
const SHELL_TRANSITION_PATHS = new Set([
    "/",
    "/holdings",
    "/data-hall",
    "/trade",
    "/announcements",
    "/user",
    "/more",
    "/market",
    "/my-assets",
    "/wallet",
    "/orders"
]);
const shellTransitionName = computed(() => SHELL_TRANSITION_PATHS.has(route.path) ? "app-shell-soft" : "app-shell-none");
const active = ref(0);
const taskRunning = ref(false);
const forceUpdateRef = ref(null);
let timer = null;
let stopActivityAssistScheduler = null;
let stopHallTradeScheduler = null;
let stopAutoOrderPayScheduler = null;
let stopWarehousePeriodicSync = null;
let tokenAlertShown = false;
const handleTokenExpired = (msg) => {
    if (tokenAlertShown)
        return;
    tokenAlertShown = true;
    clearTokenCheckCache();
    clearLstSession();
    showDialog({
        title: "\u767B\u5F55\u5DF2\u8FC7\u671F",
        message: msg || "Token \u5DF2\u5931\u6548\uFF0C\u8BF7\u91CD\u65B0\u767B\u5F55",
        confirmButtonText: "\u53BB\u767B\u5F55",
        confirmButtonColor: getPrimaryColor()
    }).then(() => {
        tokenAlertShown = false;
        router.replace("/");
    }).catch(() => {
        tokenAlertShown = false;
    });
};
const handleReactivate = () => {
    clearSession();
    window.location.reload();
};
let topMsgPoll = null;
let topMsgResizeObserver = null;
const onTopMsgWindowResize = () => syncAppTopBannerHeight();
function syncAppTopBannerHeight() {
    const root = appRootRef.value;
    if (!root)
        return;
    const strip = topMsgStripRef.value;
    const has = !!(appTopMsgText.value && String(appTopMsgText.value).trim());
    if (!has || !strip) {
        root.style.setProperty("--app-top-banner-h", "0px");
        return;
    }
    const h = Math.ceil(strip.getBoundingClientRect().height);
    root.style.setProperty("--app-top-banner-h", `${h}px`);
}
watch(appTopMsgText, () => {
    void nextTick(() => syncAppTopBannerHeight());
});
onMounted(async () => {
    void fetchAppTopMsgOnce();
    topMsgPoll = setInterval(() => {
        void fetchAppTopMsgOnce();
    }, 45 * 1000);
    await initDeviceSecurity();
    initIntegrityGuard();
    setTokenExpiredHandler(handleTokenExpired);
    const token = getLstToken();
    const phone = getLstPhone();
    if (token && phone) {
        try {
            const res = await validateLstToken();
            if (res.code !== 1) {
                handleTokenExpired(res.message || "Token \u5DF2\u8FC7\u671F");
            }
            else {
                stopWarehousePeriodicSync = startWarehousePeriodicSync();
            }
        }
        catch (e) {
            console.warn("Token \u68C0\u67E5\u5931\u8D25:", e);
        }
    }
    timer = setInterval(() => {
        taskRunning.value = getState().running;
    }, 1e3);
    stopActivityAssistScheduler = startActivityAssistantScheduler();
    stopHallTradeScheduler = startHallTradeScheduler();
    stopAutoOrderPayScheduler = startAutoOrderPayScheduler();
    startHeartbeat((url, msg, version) => {
        forceUpdateRef.value?.showForceUpdate(url, msg, version);
    }, (msg) => {
        forceUpdateRef.value?.showKicked(msg);
    }, () => {
        forceUpdateRef.value?.showExpired();
    });
    await nextTick();
    syncAppTopBannerHeight();
    const strip = topMsgStripRef.value;
    if (strip && typeof ResizeObserver !== "undefined") {
        topMsgResizeObserver = new ResizeObserver(() => syncAppTopBannerHeight());
        topMsgResizeObserver.observe(strip);
    }
    window.addEventListener("resize", onTopMsgWindowResize);
});
onUnmounted(() => {
    window.removeEventListener("resize", onTopMsgWindowResize);
    if (topMsgResizeObserver) {
        topMsgResizeObserver.disconnect();
        topMsgResizeObserver = null;
    }
    if (topMsgPoll) {
        clearInterval(topMsgPoll);
        topMsgPoll = null;
    }
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
    stopActivityAssistScheduler?.();
    stopActivityAssistScheduler = null;
    stopHallTradeScheduler?.();
    stopHallTradeScheduler = null;
    stopAutoOrderPayScheduler?.();
    stopAutoOrderPayScheduler = null;
    if (stopWarehousePeriodicSync) {
        stopWarehousePeriodicSync();
        stopWarehousePeriodicSync = null;
    }
    stopHeartbeat();
});
</script>

<style>
.app {
  height: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--app-bg-page, #f0f2f5);
  font-family: system-ui, -apple-system, 'PingFang SC', sans-serif;
  /* 子页面 fixed 顶栏用 top: var(--app-top-banner-h)，由脚本按提示条实际高度写入 */
  --app-top-banner-h: 0px;
}

/* 各页 van-nav-bar fixed 默认顶到视口 0，会盖住上方全局提示条 */
.app .van-nav-bar--fixed {
  top: var(--app-top-banner-h, 0px) !important;
}

/* 顶部提示：宽度 100% 时高度按固定宽高比（约 32:1），并计入刘海安全区 */
.app-top-msg-strip {
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 100;
  padding-top: env(safe-area-inset-top, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
  background: linear-gradient(180deg, rgba(255, 193, 7, 0.22) 0%, rgba(255, 193, 7, 0.08) 100%);
  border-bottom: 1px solid rgba(184, 134, 11, 0.2);
}
.app-top-msg-strip__ratio {
  width: 100%;
  aspect-ratio: 32 / 1;
  min-height: 28px;
  max-height: 44px;
  position: relative;
}
.app-top-msg-strip__inner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  box-sizing: border-box;
}
.app-top-msg-strip__text {
  font-size: clamp(11px, 2.8vw, 13px);
  line-height: 1.25;
  font-weight: 600;
  color: var(--app-text-primary, #323233);
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}

.app-body {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
  position: relative;
  padding-bottom: calc(var(--van-tabbar-height, 50px) + env(safe-area-inset-bottom, 0px) + 12px);
  display: flex;
  flex-direction: column;
}

.app-body.app-body--flush-tab {
  padding-bottom: 0;
}

/* 抢购上架：底栏占位从外层去掉，避免 Tab 上方多出一条「空背景」；内层页面自己留 tabbar+安全区 */
.app-body--trade {
  background: #fff;
  padding-bottom: 0;
}

.app-router-shell {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.app-view-root {
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  max-height: 100%;
}

.app-shell-soft-leave-active {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  min-height: 100%;
  z-index: 1;
}
.app-shell-soft-enter-active {
  position: relative;
  z-index: 0;
}
.app-shell-soft-enter-active {
  transition:
    opacity 0.34s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.34s cubic-bezier(0.22, 1, 0.36, 1);
}
.app-shell-soft-leave-active {
  transition:
    opacity 0.26s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.26s cubic-bezier(0.4, 0, 0.2, 1);
}
.app-shell-soft-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.app-shell-soft-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.app-shell-soft-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.app-shell-soft-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.app-shell-none-enter-active,
.app-shell-none-leave-active {
  transition: none;
}

@media (prefers-reduced-motion: reduce) {
  .app-shell-soft-enter-active,
  .app-shell-soft-leave-active {
    transition-duration: 0.01ms;
    transition-property: opacity;
  }
  .app-shell-soft-enter-from,
  .app-shell-soft-leave-to {
    transform: none;
  }
}
.app .van-tabbar {
  background: var(--app-tabbar-bg, rgba(255, 255, 255, 0.94));
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid var(--app-border-subtle, rgba(20, 24, 35, 0.08));
  box-shadow: 0 -4px 24px rgba(15, 23, 42, 0.04);
}
.app .van-tabbar-item--active {
  color: var(--app-accent, #b8860b) !important;
}

.global-route-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100002;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.global-route-progress.is-active {
  opacity: 1;
}
.global-route-progress__track {
  height: 3px;
  background: rgba(255, 140, 66, 0.12);
  overflow: hidden;
}
.global-route-progress__bar {
  height: 100%;
  width: 42%;
  border-radius: 0 2px 2px 0;
  background: linear-gradient(
    90deg,
    var(--app-buy-gradient-from, #ffa940),
    var(--app-tab-ink, #ff6b9d),
    var(--app-buy-gradient-to, #ff7a2f)
  );
  animation: global-route-shimmer 0.9s ease-in-out infinite;
  box-shadow: 0 0 12px rgba(255, 122, 47, 0.45);
}
@keyframes global-route-shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(320%);
  }
}

.task-badge {
  position: fixed;
  top: 10px;
  right: 10px;
  background: linear-gradient(135deg, #3d5a80, #2c5282);
  color: #fff;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(45, 74, 111, 0.35);
  letter-spacing: 0.02em;
}
</style>